import { useEffect, useRef, useState } from "react";

/** ---------------------- Tipos ---------------------- */
export type ClienteAPI = {
  cnpj: string;
  razao_social: string; // <<< NOVO
  nome_fantasia: string;
  email?: string | null;
  telefone?: string | null;
};

type RespAPI = {
  ok: boolean;
  meta: any;
  data: ClienteAPI[];
};

/** ---------------------- Constantes ---------------------- */
const URL = `https://api.eva-googlemarine.com/geral/empresa-clientes-completo`;
// Ajuste o caminho do await caso o seu backend seja diferente:
const URL_AWAIT = `https://api.eva-googlemarine.com/departamentos/suprimentos/standby/await`;

const POLL_FALLBACK_MS = 15000;
const ERROR_BACKOFF_MS = 5000;
const AWAIT_TIMEOUT_MS = 3000;
const AWAIT_GAP_MS = 120;

/** ---------------------- Utils ---------------------- */
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const jitter = (ms: number) => ms + Math.floor(Math.random() * 400);

function onlyDigits(s: string) {
  return String(s || "").replace(/\D+/g, "");
}

export function formatCNPJ(cnpj: string) {
  const d = onlyDigits(cnpj).padStart(14, "0").slice(-14);
  // 00.000.000/0000-00
  return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
}

/** Normaliza chaves que possam variar na API */
function normalizeClientes(arr: any[]): ClienteAPI[] {
  return (arr || []).map((x) => ({
    cnpj: x.cnpj ?? x.CNPJ ?? "",
    razao_social:
      x.razao_social ?? x.razaoSocial ?? x.razao ?? x.razao_social_nome ?? "", // <<< NOVO
    nome_fantasia:
      x.nome_fantasia ?? x.nomeFantasia ?? x.fantasia ?? x.nome ?? "",
    email: x.email ?? x.mail ?? null,
    telefone: x.telefone ?? x.tel ?? x.phone ?? null,
  }));
}

/** ---------------------- Fetchers ---------------------- */
export async function fetchClientesOnce(): Promise<{
  data: ClienteAPI[];
  meta: RespAPI["meta"] | null;
}> {
  const res = await fetch(URL, {
    method: "GET",
    headers: { Accept: "application/json" },
    cache: "no-store",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = (await res.json()) as RespAPI;

  if (!json?.ok) return { data: [], meta: json?.meta ?? null };

  const data = Array.isArray(json.data) ? normalizeClientes(json.data) : [];
  return { data, meta: json.meta ?? null };
}

type AwaitResult = { changed: boolean; versao: number | null };

async function awaitChange(
  since: number,
  signal?: AbortSignal
): Promise<AwaitResult | null> {
  try {
    const url = `${URL_AWAIT}?since=${encodeURIComponent(
      since
    )}&timeout_ms=${AWAIT_TIMEOUT_MS}`;
    const res = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      signal,
    });
    if (!res.ok) return null;
    const json = await res.json();
    const versao =
      typeof json?.versao === "number" ? (json.versao as number) : since;
    const changed = Boolean(json?.changed);
    return { changed, versao };
  } catch {
    return null;
  }
}

/** ---------------------- Hook ---------------------- */
export function useEmpresasCliente() {
  const [data, setData] = useState<ClienteAPI[]>([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState<RespAPI["meta"] | null>(null);

  const stop = useRef(false);
  const fetching = useRef(false);

  const versaoRef = useRef<number | null>(null);
  const awaitCtrlRef = useRef<AbortController | null>(null);
  const awaitSupported = useRef<boolean | null>(null);
  const awaitInFlightRef = useRef(false);

  async function carregar() {
    fetching.current = true;
    try {
      const r = await fetchClientesOnce();
      setData(r.data);
      setMeta(r.meta);
    } finally {
      fetching.current = false;
    }
  }

  useEffect(() => {
    stop.current = false;
    versaoRef.current = null;
    awaitSupported.current = null;
    awaitCtrlRef.current?.abort();

    (async () => {
      try {
        // 1) primeira carga
        await carregar();
        setLoading(false);

        // 2) capability detection do /await
        const init = await awaitChange(-1);
        if (init && typeof init.versao === "number") {
          versaoRef.current = init.versao;
          awaitSupported.current = true;
        } else {
          awaitSupported.current = false;
        }

        // 3) loop principal
        while (!stop.current) {
          while (fetching.current && !stop.current) await sleep(25);
          if (stop.current) break;

          const cur = versaoRef.current;

          if (awaitSupported.current !== false && cur != null) {
            if (awaitInFlightRef.current) {
              await sleep(50);
              continue;
            }

            const ac = new AbortController();
            awaitCtrlRef.current = ac;
            awaitInFlightRef.current = true;

            let resp: AwaitResult | null = null;
            try {
              resp = await awaitChange(cur, ac.signal);
            } catch (e: any) {
              if (e?.name !== "AbortError")
                await sleep(jitter(ERROR_BACKOFF_MS));
            } finally {
              if (awaitCtrlRef.current === ac) awaitCtrlRef.current = null;
              awaitInFlightRef.current = false;
            }

            if (stop.current) break;

            if (resp === null) {
              if (awaitSupported.current === null)
                awaitSupported.current = false;
              await sleep(POLL_FALLBACK_MS);
              continue;
            } else if (awaitSupported.current === null) {
              awaitSupported.current = true;
            }

            const avancou = (resp.versao ?? -1) > (cur ?? -1);
            if (resp.changed || avancou) {
              versaoRef.current = resp.versao ?? cur;
              await carregar();
              await sleep(20);
            } else {
              await sleep(AWAIT_GAP_MS);
            }
            continue;
          }

          // Fallback: polling simples
          await sleep(POLL_FALLBACK_MS);
          await carregar();
        }
      } catch (e) {
        if (!stop.current) console.error("useEmpresasCliente loop error:", e);
        setLoading(false);
      }
    })();

    return () => {
      stop.current = true;
      awaitCtrlRef.current?.abort();
    };
  }, []);

  const refresh = async () => {
    while (fetching.current) await sleep(25);
    await carregar();
  };

  return { data, loading, refresh, meta };
}
